import type { WorldObjectDef } from '@/world-prototype/objects/placeObject';

/**
 * Declarative Hubert backyard prop pack.
 *
 * Layouts: `layouts.desktop` / `layouts.tablet` / `layouts.mobile` — edit one
 * profile at a time in World Editor (?edit=1). Flat x/y/widthFrac mirror desktop.
 * Shed + doghouse are baked into the master plate and are NOT listed.
 */
const PROP = `${import.meta.env.BASE_URL}art/environments/jacksonville/backyard/props`;

export const BACKYARD_PROP_DEFS: readonly WorldObjectDef[] = [
  {
    id: "shed",
    title: "Storage Shed",
    icon: "🏚️",
    actionLabel: "Look",
    shape: "shed",
    enabled: true,
    x: 0.81856,
    y: 0.53834,
    widthFrac: 0.1517,
    depth: 97,
    layouts: {
      desktop: {
        x: 0.81856,
        y: 0.53834,
        widthFrac: 0.1517,
        scale: 1,
        depth: 97,
      },
      tablet: {
        x: 0.80562,
        y: 0.55464,
        widthFrac: 0.1529,
        scale: 1,
        depth: 97,
      },
      mobile: {
        x: 0.81462,
        y: 0.59547,
        widthFrac: 0.156,
        scale: 1,
        depth: 97,
      },
    },
    sprite: {
      src: `${PROP}/storage-shed-v2.png`,
      alt: "Storage shed with doghouse",
      imageWidth: 1177,
      imageHeight: 1024,
    },
    footprint: [
      { x: -0.07, y: -0.015 },
      { x: -0.018, y: 0.007 },
      { x: 0.055, y: 0.01 },
      { x: 0.078, y: -0.002 },
      { x: 0.078, y: -0.028 },
      { x: 0.018, y: -0.042 },
    ],
    stand: { x: -0.06, y: 0.05 },
    hit: { x: -0.05, y: -0.055 },
    hitRadius: 0.04,
    activationZone: {
      shape: "rect",
      x: -0.06,
      y: 0.05,
      width: 0.028,
      height: 0.014,
    },
  },
  {
    id: "tree-left",
    title: "Tree",
    icon: "🌳",
    actionLabel: "Look",
    shape: "hammock",
    interactive: false,
    enabled: true,
    x: 0.44266,
    y: 0.40345,
    widthFrac: 0.1242,
    depth: 155,
    layouts: {
      desktop: {
        x: 0.44266,
        y: 0.40345,
        widthFrac: 0.1242,
        scale: 1,
        depth: 155,
      },
      tablet: {
        x: 0.39363,
        y: 0.41572,
        widthFrac: 0.125,
        scale: 1,
        depth: 155,
      },
      mobile: {
        x: 0.4503,
        y: 0.45018,
        widthFrac: 0.1273,
        scale: 1,
        depth: 155,
      },
    },
    sprite: {
      src: `${PROP}/tree-left-v1.png`,
      alt: "Large backyard tree",
      imageWidth: 1024,
      imageHeight: 1385,
    },
    footprint: [
      { x: -0.012, y: 0.003 },
      { x: 0.012, y: 0.003 },
      { x: 0.01, y: -0.012 },
      { x: -0.01, y: -0.012 },
    ],
    stand: { x: 0, y: 0.029 },
    hitRadius: 0,
    ambient: "canopy-sway",
    groundShadow: "ellipse",
  },
  {
    id: "tree-right",
    title: "Tree",
    icon: "🌳",
    actionLabel: "Look",
    shape: "hammock",
    interactive: false,
    enabled: true,
    x: 0.59066,
    y: 0.40503,
    widthFrac: 0.1208,
    rotationDeg: -0.4,
    depth: 155,
    layouts: {
      desktop: {
        x: 0.59066,
        y: 0.40503,
        widthFrac: 0.1208,
        scale: 1,
        rotationDeg: -0.4,
        depth: 155,
      },
      tablet: {
        x: 0.53093,
        y: 0.4171,
        widthFrac: 0.1215,
        scale: 1,
        rotationDeg: -0.4,
        depth: 155,
      },
      mobile: {
        x: 0.58672,
        y: 0.44644,
        widthFrac: 0.1234,
        scale: 1,
        rotationDeg: -0.4,
        depth: 155,
      },
    },
    sprite: {
      src: `${PROP}/tree-right-v1.png`,
      alt: "Large backyard tree",
      imageWidth: 997,
      imageHeight: 1388,
    },
    footprint: [
      { x: -0.012, y: 0.003 },
      { x: 0.012, y: 0.003 },
      { x: 0.01, y: -0.012 },
      { x: -0.01, y: -0.012 },
    ],
    stand: { x: 0, y: 0.029 },
    hitRadius: 0,
    ambient: "canopy-sway",
    groundShadow: "ellipse",
  },
  {
    id: "hammock",
    title: "Hammock",
    icon: "🛏️",
    actionLabel: "Swing",
    shape: "hammock",
    enabled: true,
    x: 0.51269,
    y: 0.42243,
    widthFrac: 0.1309,
    rotationDeg: -2.29,
    depth: 128,
    layouts: {
      desktop: {
        x: 0.51269,
        y: 0.42243,
        widthFrac: 0.1309,
        scale: 1,
        rotationDeg: -2.29,
        depth: 128,
      },
      tablet: {
        x: 0.45809,
        y: 0.43409,
        widthFrac: 0.1317,
        scale: 1,
        rotationDeg: -2.29,
        depth: 128,
      },
      mobile: {
        x: 0.51737,
        y: 0.46325,
        widthFrac: 0.1337,
        scale: 1,
        rotationDeg: -2.29,
        depth: 128,
      },
    },
    sprite: {
      src: `${PROP}/hammock-v1.png`,
      alt: "Backyard hammock",
      imageWidth: 1324,
      imageHeight: 647,
    },
    footprint: [],
    stand: { x: 0, y: 0.042 },
    hit: { x: 0, y: -0.02 },
    hitRadius: 0.01,
    activationZone: {
      shape: "circle",
      x: 0,
      y: 0.042,
      radius: 0.016,
    },
    ambient: "hammock-sway",
  },
  {
    id: "fire-pit",
    title: "Fire Pit",
    icon: "🔥",
    actionLabel: "Look",
    shape: "fire-pit",
    enabled: true,
    x: 0.3742,
    y: 0.52382,
    widthFrac: 0.0376,
    rotationDeg: -0.23,
    layouts: {
      desktop: {
        x: 0.3742,
        y: 0.52382,
        widthFrac: 0.0376,
        scale: 1,
        rotationDeg: -0.23,
      },
      tablet: {
        x: 0.33456,
        y: 0.52671,
        widthFrac: 0.0376,
        scale: 1,
        rotationDeg: -0.23,
      },
      mobile: {
        x: 0.36236,
        y: 0.47614,
        widthFrac: 0.0367,
        scale: 1,
        rotationDeg: -0.23,
      },
    },
    sprite: {
      src: `${PROP}/fire-pit-v1.png`,
      alt: "Stone fire pit",
      imageWidth: 903,
      imageHeight: 729,
    },
    footprint: [
      { x: -0.017, y: 0.005 },
      { x: 0.017, y: 0.005 },
      { x: 0.015, y: -0.014 },
      { x: -0.015, y: -0.014 },
    ],
    stand: { x: 0, y: 0.03 },
    hit: { x: 0, y: -0.038 },
    hitRadius: 0.02,
    activationZone: {
      shape: "circle",
      x: 0,
      y: 0.03,
      radius: 0.015,
    },
    groundShadow: "soft",
  },
  {
    id: "garden-bed",
    title: "Garden",
    icon: "🌽",
    actionLabel: "Look",
    shape: "garden",
    enabled: true,
    x: 0.27468,
    y: 0.68541,
    widthFrac: 0.0393,
    rotationDeg: -0.98,
    layouts: {
      desktop: {
        x: 0.27468,
        y: 0.68541,
        widthFrac: 0.0393,
        scale: 1,
        rotationDeg: -0.98,
      },
      tablet: {
        x: 0.23413,
        y: 0.69013,
        widthFrac: 0.0394,
        scale: 1,
        rotationDeg: -0.35,
      },
      mobile: {
        x: 0.25535,
        y: 0.56927,
        widthFrac: 0.0607,
        scale: 1.634,
        rotationDeg: -0.35,
      },
    },
    gardenType: "vegetables",
    sprite: {
      src: `${PROP}/garden-bed-v1.png`,
      alt: "Raised garden with corn and tomatoes",
      imageWidth: 1461,
      imageHeight: 776,
    },
    spriteVariants: {
      vegetables: {
        src: `${PROP}/garden-bed-v1.png`,
        alt: "Raised garden with corn and tomatoes",
        imageWidth: 1461,
        imageHeight: 776,
      },
      flowers: {
        src: `${PROP}/garden-bed-flowers-v1.png`,
        alt: "Raised flower garden bed",
        imageWidth: 1460,
        imageHeight: 826,
      },
    },
    footprint: [
      { x: -0.038, y: 0.007 },
      { x: 0.038, y: 0.007 },
      { x: 0.034, y: -0.018 },
      { x: -0.034, y: -0.018 },
    ],
    stand: { x: 0.03, y: 0.023 },
    hit: { x: 0.015, y: -0.058 },
    hitRadius: 0.026,
    activationZone: {
      shape: "circle",
      x: 0.03,
      y: 0.023,
      radius: 0.016,
    },
    ambient: "leaf-sway",
    groundShadow: "soft",
  },
  {
    id: "clothesline",
    title: "Clothesline",
    icon: "👕",
    actionLabel: "Look",
    shape: "clothesline",
    enabled: true,
    x: 0.20241,
    y: 0.9579,
    widthFrac: 0.1949,
    rotationDeg: -0.73,
    layouts: {
      desktop: {
        x: 0.20241,
        y: 0.9579,
        widthFrac: 0.1949,
        scale: 1,
        rotationDeg: -0.73,
      },
      tablet: {
        x: 0.18093,
        y: 0.94562,
        widthFrac: 0.1949,
        scale: 1,
        rotationDeg: 1.33,
      },
      mobile: {
        x: 0.21524,
        y: 0.7956,
        widthFrac: 0.188,
        scale: 1,
        rotationDeg: 1.33,
      },
    },
    sprite: {
      src: `${PROP}/clothesline-v1.png`,
      alt: "Backyard clothesline",
      imageWidth: 1207,
      imageHeight: 773,
    },
    footprint: [
      { x: -0.037, y: 0.004 },
      { x: 0.037, y: 0.004 },
      { x: 0.034, y: -0.009 },
      { x: -0.034, y: -0.009 },
    ],
    stand: { x: 0.02, y: 0.028 },
    hit: { x: 0.02, y: -0.11 },
    hitRadius: 0.042,
    activationZone: {
      shape: "circle",
      x: 0.02,
      y: 0.028,
      radius: 0.016,
    },
    ambient: "clothes-sway",
    groundShadow: "soft",
  },
  {
    id: "little-pine-tree",
    title: "Little Pine",
    icon: "🌲",
    actionLabel: "Look",
    shape: "hammock",
    interactive: false,
    enabled: false,
    x: 0.68665,
    y: 0.71179,
    widthFrac: 0.05,
    layouts: {
      desktop: {
        x: 0.68665,
        y: 0.71179,
        widthFrac: 0.05,
      },
      tablet: {
        x: 0.68665,
        y: 0.71179,
        widthFrac: 0.05,
      },
      mobile: {
        x: 0.68665,
        y: 0.71179,
        widthFrac: 0.05,
      },
    },
    sprite: {
      src: `${PROP}/little-pine-tree-v1.png`,
      alt: "Small backyard pine tree",
      imageWidth: 500,
      imageHeight: 880,
    },
    footprint: [
      { x: -0.0025, y: 0.001 },
      { x: 0.0025, y: 0.001 },
      { x: 0.002, y: -0.0025 },
      { x: -0.002, y: -0.0025 },
    ],
    stand: { x: 0.008, y: 0.012 },
    hitRadius: 0,
    ambient: "canopy-sway",
    groundShadow: "ellipse",
  },
  {
    id: "little-pine-tree-2",
    title: "Little Pine",
    icon: "🌲",
    actionLabel: "Look",
    shape: "hammock",
    interactive: false,
    enabled: false,
    x: 0.86763,
    y: 0.90162,
    widthFrac: 0.05,
    rotationDeg: 1.59,
    layouts: {
      desktop: {
        x: 0.86763,
        y: 0.90162,
        widthFrac: 0.05,
        rotationDeg: 1.59,
      },
      tablet: {
        x: 0.86763,
        y: 0.90162,
        widthFrac: 0.05,
        rotationDeg: 1.59,
      },
      mobile: {
        x: 0.86763,
        y: 0.90162,
        widthFrac: 0.05,
        rotationDeg: 1.59,
      },
    },
    sprite: {
      src: `${PROP}/little-pine-tree-v1.png`,
      alt: "Small backyard pine tree",
      imageWidth: 500,
      imageHeight: 880,
    },
    footprint: [
      { x: -0.0025, y: 0.001 },
      { x: 0.0025, y: 0.001 },
      { x: 0.002, y: -0.0025 },
      { x: -0.002, y: -0.0025 },
    ],
    stand: { x: 0.008, y: 0.012 },
    hitRadius: 0,
    ambient: "canopy-sway",
    groundShadow: "ellipse",
  },
  {
    id: "little-pine-tree-3",
    title: "Little Pine",
    icon: "🌲",
    actionLabel: "Look",
    shape: "hammock",
    interactive: false,
    enabled: true,
    x: 0.28172,
    y: 0.45906,
    widthFrac: 0.0382,
    rotationDeg: 3.5,
    layouts: {
      desktop: {
        x: 0.28172,
        y: 0.45906,
        widthFrac: 0.0382,
        scale: 1,
        rotationDeg: 3.5,
      },
      tablet: {
        x: 0.25561,
        y: 0.45153,
        widthFrac: 0.0381,
        scale: 1,
        rotationDeg: 3.5,
      },
      mobile: {
        x: 0.26881,
        y: 0.45625,
        widthFrac: 0.0382,
        scale: 1,
        rotationDeg: 3.5,
      },
    },
    sprite: {
      src: `${PROP}/little-pine-tree-v1.png`,
      alt: "Small backyard pine tree",
      imageWidth: 500,
      imageHeight: 880,
    },
    footprint: [
      { x: -0.0025, y: 0.001 },
      { x: 0.0025, y: 0.001 },
      { x: 0.002, y: -0.0025 },
      { x: -0.002, y: -0.0025 },
    ],
    stand: { x: 0.008, y: 0.012 },
    hitRadius: 0,
    ambient: "canopy-sway",
    groundShadow: "ellipse",
  },
  {
    id: "little-pine-tree-4",
    title: "Little Pine",
    icon: "🌲",
    actionLabel: "Look",
    shape: "hammock",
    interactive: false,
    enabled: false,
    x: 0.68963,
    y: 0.71867,
    widthFrac: 0.05,
    layouts: {
      desktop: {
        x: 0.68963,
        y: 0.71867,
        widthFrac: 0.05,
      },
      tablet: {
        x: 0.22,
        y: 0.54,
        widthFrac: 0.05,
        rotationDeg: -2.5,
      },
      mobile: {
        x: 0.22,
        y: 0.54,
        widthFrac: 0.05,
        rotationDeg: -2.5,
      },
    },
    sprite: {
      src: `${PROP}/little-pine-tree-v1.png`,
      alt: "Small backyard pine tree",
      imageWidth: 500,
      imageHeight: 880,
    },
    footprint: [
      { x: -0.0025, y: 0.001 },
      { x: 0.0025, y: 0.001 },
      { x: 0.002, y: -0.0025 },
      { x: -0.002, y: -0.0025 },
    ],
    stand: { x: 0.008, y: 0.012 },
    hitRadius: 0,
    ambient: "canopy-sway",
    groundShadow: "ellipse",
  },
];
